/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
  DatePicker,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import CurrencyInput from 'components/CurrencyInput';
import FormSubmitControls from 'components/FormSubmitControls';
import currencyFormatter from 'utils/currencyFormatter';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import {
  getEstadosGlobales,
} from 'api/catalogos/index';
import {
  getSalariosMinimosMunicipales,
  postSalarioMinimoMunicipal,
  deleteSalarioMinimoMunicipal,
  patchSalarioMinimoMunicipal,
  getSalarioMinimoMunicipal,
} from 'api/nominas/catalogos/salariosMinimosMunicipales';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_INACTIVO,
}
  from 'utils/constants';
import { CheckCircleOutlined } from '@ant-design/icons';
import ModalConfirmation from 'components/ModalConfirmation';

export const salarioMinimoMunicipalPermission = {
  permissionModel: 'salariominimomunicipal',
  catalogsModels: [],
  tabsModels: [],
};

const SalarioMinimoMunicipal = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [disableAuth, setDisableAuth] = useState(false);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const [visibleAuthConfirmation, setVisibleAuthConfirmation] = useState(false);
  const [visibleAlertInactive, setVisibleAlertInactive] = useState(false);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getSalariosMinimosMunicipales();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getSalariosMinimosMunicipales(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    setDisableForm(false);
    form.resetFields();
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede Autorizar un Sindicato ya Autorizado');
        return;
      }
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchSalarioMinimoMunicipal(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleAuthConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const isDisabled = (rowEstatus) => {
    if (rowEstatus !== ESTATUS_ACTIVO) {
      setDisableForm(true);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _salario = await getSalarioMinimoMunicipal(match.id);
    setTimeout(() => {
      if (_salario) {
        form.resetFields();
        setTimeout(() => {
          isDisabled(_salario?.estados_globales);
          form.setFieldsValue(formatReceived(_salario));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };

      values.ano_salario_municipal.set('month', 0);
      values.ano_salario_municipal.set('date', 1);

      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchSalarioMinimoMunicipal(key, values) : postSalarioMinimoMunicipal(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteSalarioMinimoMunicipal(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    setDeleteDisabled(isAutorizado);
    setDisableAuth(isAutorizado || isInactivo);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Año',
      dataIndex: 'ano_salario_municipal',
      key: 'ano_salario_municipal',
      width: 200,
      render: (val) => val?.format('YYYY'),
    },
    {
      titleText: 'Salario Mínimo Municipal',
      dataIndex: 'salario_minimo_municipal',
      key: 'salario_minimo_municipal',
      width: 200,
      align: 'Right',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Vigencia Inicial',
      dataIndex: 'vigencia_inicial',
      key: 'vigencia_inicial',
      width: 250,
      render: (val) => val?.format(),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 250,
      render: (val) => val?.format(),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    vigencia_inicial: [
      requiredRule,
      {
        validator: async () => {
          const { vigencia_inicial, ano_salario_municipal, vigencia_final } = form.getFieldsValue();
          if (vigencia_inicial?.year() < ano_salario_municipal?.year()
            || vigencia_inicial?.year() > ano_salario_municipal?.year()) {
            throw new Error('La Vigencia Inicial debe estar dentro del Año seleccionado');
          }
          if (vigencia_inicial > vigencia_final) {
            throw new Error('La Vigencia Inicial debe ser menor a la Vigencia Final');
          }
        },
      },
    ],
    vigencia_final: [
      requiredRule,
      {
        validator: async () => {
          const { vigencia_final, ano_salario_municipal, vigencia_inicial } = form.getFieldsValue();
          if (vigencia_final?.year() < ano_salario_municipal?.year()
            || vigencia_final?.year() > ano_salario_municipal?.year()) {
            throw new Error('La Vigencia Final debe estar dentro del Año seleccionado');
          }
          if (vigencia_final < vigencia_inicial) {
            throw new Error('La Vigencia Final debe ser mayor a la Vigencia Inicial');
          }
        },
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            permission={permission}
            allowExportPDF={false}
            customActions={[
              {
                onClick: () => setVisibleAuthConfirmation(true),
                permission: { model: 'salariominimomunicipal', action: 'autorizar' },
                text: 'Autorizar',
                icon: <CheckCircleOutlined />,
                disabledBySelection: false,
                className: 'success',
                // eslint-disable-next-line max-len
                disabled: !selectedRowKeys?.length || disableAuth,
              },
            ]}
            disableDelete={
              deleteDisabled
            }
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Salario Mínimo Municipal`}
            extra={(
              <FormSubmitControls
                onFinish={() => form.submit()}
                onCancel={onCancel}
                loading={loading}
                allowSave={!selectedRowKeys?.length || !disableAuth}
              />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError
              name="general"
              disabled={disableForm}
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="ano_salario_municipal"
                    label="Año"
                    hasFeedback
                    rules={rules.required}
                  >
                    <DatePicker
                      picker="year"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <CurrencyInput
                    label="Salario Mínimo Municipal"
                    name="salario_minimo_municipal"
                    rules={rules.required}
                  />
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="vigencia_inicial"
                    label="Vigencia Inicial"
                    hasFeedback
                    rules={rules.vigencia_inicial}
                  >
                    <DatePicker
                      allowClear
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="vigencia_final"
                    label="Vigencia Final"
                    hasFeedback
                    rules={rules.vigencia_final}
                  >
                    <DatePicker
                      allowClear
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="estados_globales"
                    label="Estatus"
                    rules={rules.required}
                    hasFeedback
                    initialValue={1}
                  >
                    <Select
                      labelProp="descripcion"
                      keyProp="id"
                      activeInactive={!deleteDisabled}
                      dataSource={estados}
                      disabled={selectedRowKeys.length <= 0}
                      onChange={(val) => {
                        if (val === ESTATUS_INACTIVO) {
                          setVisibleAlertInactive(true);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Salario Mínimo Municipal ${form.getFieldValue('ano_salario_municipal')?.format('YYYY')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleAlertInactive(false)}
          ConfirmInactive
          onCancel={() => {
            setVisibleAlertInactive(false);
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
          }}
          open={visibleAlertInactive}
          contentTitle={`Inactivar el Salario Mínimo Municipal ${form.getFieldValue('ano_salario_municipal')?.format('YYYY')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setVisibleAuthConfirmation(false);
            onCancel();
          }}
          open={visibleAuthConfirmation}
          contentTitle={`Autorizar el Salario Mínimo Municipal ${form.getFieldValue('ano_salario_municipal')?.format('YYYY')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default SalarioMinimoMunicipal;
